import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { cn } from '../lib/utils';
import {
  Plus,
  Trash2,
  ChevronDown,
  Split,
  ShoppingCart,
  Loader2,
} from 'lucide-react';
import salesService from '../services/salesService';
import purchaseService from '../services/purchaseService';
import inventoryService from '../services/inventoryService';

// Custom Autocomplete for suppliers
const SupplierAutocomplete = ({ suppliers, value, onChange, placeholder }) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const selectedSupplier = suppliers.find(s => (s._id || s.supplier_id) === value);

  useEffect(() => {
    setInputValue(selectedSupplier?.supplier_name || '');
  }, [selectedSupplier]);

  const filteredSuppliers = suppliers.filter(s =>
    s.supplier_name?.toLowerCase().includes(inputValue.toLowerCase())
  );

  return (
    <div className="relative">
      <Input
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        className="w-full"
      />
      {isOpen && filteredSuppliers.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredSuppliers.map((supplier) => (
            <div
              key={supplier._id || supplier.supplier_id}
              className={cn(
                "px-3 py-2 cursor-pointer hover:bg-muted",
                (supplier._id || supplier.supplier_id) === value && "bg-muted"
              )}
              onClick={() => {
                onChange(supplier._id || supplier.supplier_id);
                setInputValue(supplier.supplier_name);
                setIsOpen(false);
              }}
            >
              {supplier.supplier_name}
            </div>
          ))}
        </div>
      )}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

const SoPoSplittingDialog = ({ open, onClose, salesOrderId, salesOrderData, onConvert }) => {
  // Stepper states
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Data states
  const [suppliers, setSuppliers] = useState([]);
  const [splittingStrategy, setSplittingStrategy] = useState('manual');
  const [poAssignments, setPoAssignments] = useState([]);
  const [conversionMetadata, setConversionMetadata] = useState({
    reason: '',
    priority: 'medium',
    notes: ''
  });

  // UI states
  const [validationErrors, setValidationErrors] = useState([]);
  const [previewData, setPreviewData] = useState(null);

  // Inventory states
  const [inventoryData, setInventoryData] = useState({});

  const steps = ['配置分拆策略', '分配供应商', '设置价格', '预览确认'];

  const initializeData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Load suppliers
      const suppliersResponse = await purchaseService.getSuppliers(1, 100);
      const supplierList = Array.isArray(suppliersResponse)
        ? suppliersResponse
        : (suppliersResponse.suppliers || []);
      setSuppliers(supplierList);

      // Use new unified execution-progress API to get accurate allocation data
      let executionProgress = null;
      try {
        const soIdForProgress = salesOrderData?._id || salesOrderData?.sales_order_id || salesOrderId;
        if (soIdForProgress) {
          executionProgress = await salesService.getExecutionProgress(soIdForProgress);
        }
      } catch (e) {
        console.warn('Failed to fetch execution progress for remaining calculation:', e);
      }

      // Fetch inventory data for all products
      let inventoryMap = {};
      if (salesOrderData?.products) {
        try {
          const productCodes = salesOrderData.products.map(p => p.product_code || p.product_id);
          const clientId = salesOrderData.client_ref || salesOrderData.client_id;

          const inventoryResponse = await inventoryService.batchLookupInventory(productCodes, clientId);
          inventoryMap = inventoryResponse.inventory || {};
          setInventoryData(inventoryMap);
        } catch (err) {
          console.error('Failed to fetch inventory data:', err);
        }
      }

      // Initialize PO assignments based on execution progress data
      if (salesOrderData?.products && executionProgress?.per_product) {
        const initialLineItems = salesOrderData.products
          .map((product, index) => {
            const productCode = product.product_code || product.product_id;
            // Use line_item_id if available (supports duplicate product_id + specification),
            // fallback to _id for backward compatibility with existing data
            const soLineId = product.line_item_id || String(product._id || product.so_line_item_id || '');

            const productProgress = executionProgress.per_product[productCode] || {};

            const originalQuantity = Number(product.quantity || 0);
            const pendingPurchase = Number(productProgress.pending_po_creation || 0);
            const suggestedPurchase = pendingPurchase > 0 ? pendingPurchase : 0;

            return {
              line_item_id: `init-${Date.now()}-${index}`,
              product_code: productCode,
              product_name: product.product_name || product.name,
              so_line_item_id: soLineId,
              original_quantity: originalQuantity,
              so_quantity: pendingPurchase,
              allocated_quantity: suggestedPurchase,
              unit_price: product.unit_price || product.price || 0,
              currency: 'CNY',
              fx_rate: 1.0,
              split_reason: '',
              inventory: {
                available_quantity: Number(productProgress.stock_at_creation || 0),
                suggested_purchase: suggestedPurchase,
                can_fulfill_from_stock: Number(productProgress.from_stock || 0) >= Number(productProgress.required_qty || 0)
              }
            };
          })
          // Show all items - users can manually select which to include in PO

        const initialAssignments = [{
          id: Date.now(),
          supplier_id: '',
          supplier_name: '',
          purchase_order_id: `PO-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
          line_items: initialLineItems
        }];
        setPoAssignments(initialAssignments);
      }
    } catch (err) {
      setError('加载数据失败: ' + (err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  }, [salesOrderData, salesOrderId]);

  useEffect(() => {
    if (open && salesOrderData) {
      initializeData();
    }
  }, [open, salesOrderData, initializeData]);

  const addPoAssignment = async () => {
    if (!salesOrderData?.products) {
      setError('无法添加采购订单：销售订单数据不完整');
      return;
    }

    let executionProgress = null;
    try {
      const soIdForProgress = salesOrderData?._id || salesOrderData?.sales_order_id || salesOrderId;
      if (soIdForProgress) {
        executionProgress = await salesService.getExecutionProgress(soIdForProgress);
      }
    } catch (e) {
      console.warn('Failed to fetch execution progress for remaining calculation (add).');
    }

    const allocatedInDialog = {};
    poAssignments.forEach(a => {
      a.line_items.forEach(item => {
        const code = item.product_code;
        const qty = parseFloat(item.allocated_quantity) || 0;
        allocatedInDialog[code] = (allocatedInDialog[code] || 0) + qty;
      });
    });

    const assignmentId = Date.now();
    const newAssignment = {
      id: assignmentId,
      supplier_id: '',
      supplier_name: '',
      purchase_order_id: `PO-${new Date().getFullYear()}-${String(assignmentId).slice(-6)}`,
      line_items: salesOrderData.products
        .map((product, index) => {
          const code = product.product_code || product.product_id;
          // Use line_item_id if available (supports duplicate product_id + specification),
          // fallback to _id for backward compatibility with existing data
          const soLineId = product.line_item_id || String(product._id || product.so_line_item_id || '');

          const originalQuantity = Number(product.quantity || 0);
          const productProgress = executionProgress?.per_product?.[code] || {};

          const pendingFromBackend = Number(productProgress.pending_po_creation || 0);
          const planned = Number(allocatedInDialog[code] || 0);
          const remaining = Math.max(pendingFromBackend - planned, 0);

          const inventory = inventoryData[code] || {};
          const availableStock = Number(productProgress.stock_at_creation || inventory.available_quantity || 0);

          return {
            ...product,
            line_item_id: `add-${assignmentId}-${index}`,
            product_code: code,
            product_name: product.product_name || product.name,
            so_line_item_id: soLineId,
            original_quantity: originalQuantity,
            so_quantity: remaining,
            allocated_quantity: remaining,
            unit_price: product.unit_price || product.price || 0,
            currency: 'CNY',
            fx_rate: 1.0,
            split_reason: '',
            inventory: {
              available_quantity: availableStock,
              suggested_purchase: remaining,
              can_fulfill_from_stock: Number(productProgress.from_stock || 0) >= Number(productProgress.required_qty || 0)
            }
          };
        })
        // Show all items - users can manually select which to include in PO
    };
    setPoAssignments(prev => [...prev, newAssignment]);
  };

  const removePoAssignment = (assignmentId) => {
    setPoAssignments(poAssignments.filter(assignment => assignment.id !== assignmentId));
  };

  const updatePoAssignment = (assignmentId, field, value) => {
    setPoAssignments(assignments =>
      assignments.map(assignment => {
        if (assignment.id === assignmentId) {
          const updated = { ...assignment, [field]: value };

          if (field === 'supplier_id') {
            const supplier = suppliers.find(s => (s._id === value) || (s.supplier_id === value));
            updated.supplier_name = supplier?.supplier_name || '';
          }

          return updated;
        }
        return assignment;
      })
    );
  };

  const updateLineItem = (assignmentId, lineItemId, field, value) => {
    setPoAssignments(assignments =>
      assignments.map(assignment => {
        if (assignment.id === assignmentId) {
          return {
            ...assignment,
            line_items: assignment.line_items.map(item => {
              if (item.line_item_id === lineItemId) {
                return { ...item, [field]: value };
              }
              return item;
            })
          };
        }
        return assignment;
      })
    );
  };

  const validateCurrentStep = useCallback(() => {
    const errors = [];

    switch (activeStep) {
      case 0:
        if (!splittingStrategy) {
          errors.push('请选择分拆策略');
        }
        break;

      case 1:
        if (poAssignments.length === 0) {
          errors.push('至少需要一个采购订单分配');
          break;
        }
        poAssignments.forEach((assignment, index) => {
          if (!assignment.supplier_id) {
            errors.push(`采购订单 ${index + 1} 必须选择供应商`);
          }
        });
        break;

      case 2:
        const productTotals = {};
        if (poAssignments.length > 0 && poAssignments[0].line_items) {
          poAssignments[0].line_items.forEach(item => {
            const productCode = item.product_code;
            productTotals[productCode] = {
              required: item.original_quantity || 0,
              allocated: 0
            };
          });
        }

        poAssignments.forEach((assignment, assignmentIndex) => {
          assignment.line_items.forEach((item) => {
            const productCode = item.product_code;
            if (productTotals[productCode]) {
              productTotals[productCode].allocated += item.allocated_quantity || 0;
            }

            const alloc = parseFloat(item.allocated_quantity) || 0;
            if (alloc > 0 && (!item.unit_price || item.unit_price <= 0)) {
              errors.push(`采购订单 ${assignmentIndex + 1} 产品 ${productCode} 单价必须大于0（已分配数量>0）`);
            }
          });
        });
        break;

      case 3:
        if (poAssignments.length === 0) {
          errors.push('至少需要一个采购订单分配');
          break;
        }

        poAssignments.forEach((assignment, index) => {
          if (!assignment.supplier_id) {
            errors.push(`采购订单 ${index + 1} 必须选择供应商`);
          }
        });
        break;

      default:
        break;
    }

    return errors;
  }, [activeStep, splittingStrategy, poAssignments]);

  const generatePreview = useCallback(() => {
    const preview = {
      sales_order_id: salesOrderData?.sales_order_id,
      total_pos: poAssignments.length,
      po_summaries: [],
      total_amount: 0
    };

    poAssignments.forEach((assignment, index) => {
      const activeItems = assignment.line_items.filter(item => item.allocated_quantity > 0);
      const poTotal = activeItems.reduce((sum, item) =>
        sum + (item.allocated_quantity * item.unit_price * item.fx_rate), 0
      );

      preview.po_summaries.push({
        po_index: index + 1,
        supplier_name: assignment.supplier_name,
        line_item_count: activeItems.length,
        total_amount: poTotal,
        currency_breakdown: activeItems.reduce((acc, item) => {
          const currency = item.currency || 'CNY';
          const amount = item.allocated_quantity * item.unit_price;
          acc[currency] = (acc[currency] || 0) + amount;
          return acc;
        }, {})
      });

      preview.total_amount += poTotal;
    });

    return preview;
  }, [poAssignments, salesOrderData]);

  const handleNext = () => {
    const errors = validateCurrentStep();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors([]);
    if (activeStep === steps.length - 2) {
      setPreviewData(generatePreview());
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleConvert = async () => {
    try {
      setLoading(true);
      setError('');

      const requiredTotals = {};
      if (poAssignments.length > 0 && poAssignments[0].line_items) {
        poAssignments[0].line_items.forEach(item => {
          const code = item.product_code;
          requiredTotals[code] = item.original_quantity || 0;
        });
      }

      const allocatedTotals = {};
      poAssignments.forEach(a => {
        a.line_items.forEach(item => {
          const code = item.product_code;
          const qty = parseFloat(item.allocated_quantity) || 0;
          allocatedTotals[code] = (allocatedTotals[code] || 0) + qty;
        });
      });

      const hasPartial = Object.keys(requiredTotals).some(code => {
        const required = requiredTotals[code] || 0;
        const allocated = allocatedTotals[code] || 0;
        return allocated < required;
      });

      const conversionData = {
        splitting_strategy: splittingStrategy,
        manual_splits: poAssignments
          .filter(assignment => assignment.supplier_id)
          .map(assignment => ({
            supplier_id: assignment.supplier_id,
            supplier_name: assignment.supplier_name,
            purchase_order_id: assignment.purchase_order_id || '',
            notes: assignment.notes || '',
            line_items: assignment.line_items
              .filter(item => item.allocated_quantity > 0)
              .map(item => ({
                product_code: item.product_code,
                product_name: item.product_name,
                quantity: item.allocated_quantity,
                unit_price: item.unit_price,
                currency: item.currency,
                fx_rate: item.fx_rate,
                split_reason: item.split_reason
              }))
          }))
          .filter(group => Array.isArray(group.line_items) && group.line_items.length > 0),
        conversion_metadata: {
          ...conversionMetadata,
          created_from_ui: true,
          timestamp: new Date().toISOString()
        },
        idempotency_key: `so-split-${salesOrderId}-${Date.now()}`,
        force_create: hasPartial === true
      };

      const legacyPayload = {
        po_assignments: conversionData.manual_splits.map(group => ({
          supplier_id: group.supplier_id,
          supplier_name: group.supplier_name,
          purchase_order_id: group.purchase_order_id || '',
          line_items: group.line_items.map(li => ({
            product_code: li.product_code,
            product_name: li.product_name,
            quantity: li.quantity,
            unit_price: li.unit_price,
            currency: li.currency,
            fx_rate: li.fx_rate,
            split_reason: li.split_reason
          }))
        })),
        conversion_metadata: conversionData.conversion_metadata,
        idempotency_key: conversionData.idempotency_key
      };

      let result = await salesService.convertSalesOrderToPurchaseOrders(
        salesOrderData?.sales_order_id || salesOrderId,
        legacyPayload
      );

      if (!result || (Array.isArray(result.created_purchase_orders) && result.created_purchase_orders.length === 0)) {
        const manualAssignments = {};
        poAssignments.forEach(a => {
          if (!a.supplier_id) return;
          a.line_items.forEach(item => {
            const qty = parseFloat(item.allocated_quantity) || 0;
            if (qty <= 0) return;
            manualAssignments[item.product_code] = {
              supplier_id: a.supplier_id,
              supplier_name: a.supplier_name,
              estimated_cost: item.unit_price
            };
          });
        });

        if (Object.keys(manualAssignments).length > 0 && salesOrderData?._id) {
          try {
            result = await purchaseService.convertSalesOrderToPurchase(
              salesOrderData._id,
              {
                force_create: true,
                manual_supplier_assignments: manualAssignments
              }
            );
          } catch (fallbackErr) {
            // If fallback also fails, surface the original error below
          }
        }
      }

      if (onConvert && result) {
        onConvert(result);
      }

      onClose();
    } catch (err) {
      setError('转换失败: ' + (err.response?.data?.error || err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return renderStrategyStep();
      case 1:
        return renderSupplierAssignmentStep();
      case 2:
        return renderPricingStep();
      case 3:
        return renderPreviewStep();
      default:
        return null;
    }
  };

  const renderStrategyStep = () => (
    <div className="py-4 space-y-4">
      <h3 className="text-lg font-semibold">选择分拆策略</h3>

      <div className="space-y-2">
        <Label>分拆策略</Label>
        <Select value={splittingStrategy} onValueChange={setSplittingStrategy}>
          <SelectTrigger>
            <SelectValue placeholder="选择分拆策略" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="manual">手动分拆</SelectItem>
            <SelectItem value="automatic" disabled>自动分拆 (开发中)</SelectItem>
            <SelectItem value="hybrid" disabled>混合模式 (开发中)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>分拆原因</Label>
        <Textarea
          value={conversionMetadata.reason}
          onChange={(e) => setConversionMetadata(prev => ({
            ...prev,
            reason: e.target.value
          }))}
          rows={3}
          placeholder="请输入分拆原因..."
        />
      </div>

      <div className="space-y-2">
        <Label>优先级</Label>
        <Select
          value={conversionMetadata.priority}
          onValueChange={(value) => setConversionMetadata(prev => ({ ...prev, priority: value }))}
        >
          <SelectTrigger>
            <SelectValue placeholder="选择优先级" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="low">低</SelectItem>
            <SelectItem value="medium">中</SelectItem>
            <SelectItem value="high">高</SelectItem>
            <SelectItem value="urgent">紧急</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  const renderSupplierAssignmentStep = () => (
    <div className="py-4 space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">分配供应商</h3>
        <span className="text-sm text-muted-foreground">
          可分配产品：{poAssignments?.[0]?.line_items?.length || 0} 个
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={addPoAssignment}
        >
          <Plus className="h-4 w-4 mr-1" />
          添加采购订单
        </Button>
      </div>

      {poAssignments.map((assignment, index) => (
        <Card key={assignment.id}>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">采购订单 #{index + 1}</h4>
              {poAssignments.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removePoAssignment(assignment.id)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="space-y-2">
              <Label>选择供应商 *</Label>
              <SupplierAutocomplete
                suppliers={suppliers}
                value={assignment.supplier_id}
                onChange={(value) => updatePoAssignment(assignment.id, 'supplier_id', value)}
                placeholder="搜索并选择供应商"
              />
            </div>

            <div className="space-y-2">
              <Label>采购订单编号</Label>
              <Input
                value={assignment.purchase_order_id || ''}
                onChange={(e) => updatePoAssignment(assignment.id, 'purchase_order_id', e.target.value)}
                placeholder="输入采购订单编号"
              />
              <p className="text-xs text-muted-foreground">可自定义采购订单编号</p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderPricingStep = () => {
    const inventorySummary = (poAssignments[0]?.line_items || []).reduce((acc, item) => {
      const available = item.inventory?.available_quantity || 0;
      const originalRequired = item.original_quantity || 0;
      const needsPurchase = item.so_quantity || 0;

      acc.totalRequired += originalRequired;
      acc.totalAvailable += available;
      acc.totalNeedsPurchase += needsPurchase;

      if (available >= originalRequired) {
        acc.canFulfillFull += 1;
      } else if (available > 0) {
        acc.hasPartialStock += 1;
      } else {
        acc.noStock += 1;
      }

      return acc;
    }, {
      canFulfillFull: 0,
      hasPartialStock: 0,
      noStock: 0,
      totalRequired: 0,
      totalAvailable: 0,
      totalNeedsPurchase: 0
    });

    return (
      <div className="py-4 space-y-4">
        <h3 className="text-lg font-semibold">设置数量和价格</h3>

        {/* Inventory Summary */}
        <Card className="bg-primary/10 border-primary/20">
          <CardContent className="p-4">
            <h4 className="font-medium mb-3">库存可用性分析</h4>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-2">
                <div className="text-3xl font-bold text-success">
                  {inventorySummary.canFulfillFull}
                </div>
                <div className="text-sm text-muted-foreground">可完全满足</div>
              </div>
              <div className="text-center p-2">
                <div className="text-3xl font-bold text-amber-600">
                  {inventorySummary.hasPartialStock}
                </div>
                <div className="text-sm text-muted-foreground">有部分库存</div>
              </div>
              <div className="text-center p-2">
                <div className="text-3xl font-bold text-destructive">
                  {inventorySummary.noStock}
                </div>
                <div className="text-sm text-muted-foreground">无库存</div>
              </div>
            </div>
            <Separator className="my-3" />
            <div className="flex justify-around flex-wrap gap-2">
              <div>
                <div className="text-xs text-muted-foreground">总需求</div>
                <div className="font-medium">{inventorySummary.totalRequired} 件</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">库存可用</div>
                <div className="font-medium text-success">{inventorySummary.totalAvailable} 件</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">需采购</div>
                <div className="font-medium text-primary">{inventorySummary.totalNeedsPurchase} 件</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Alert>
          <AlertDescription>
            分配数量可以超过需求数量，超出部分将在收货后自动添加到库存中。
          </AlertDescription>
        </Alert>

        {poAssignments.map((assignment, assignmentIndex) => {
          if (!assignment.supplier_id) return null;

          return (
            <Collapsible key={assignment.id} defaultOpen={assignmentIndex === 0}>
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted rounded-lg hover:bg-muted/80">
                <span className="font-medium">
                  {assignment.supplier_name} - 采购订单 #{assignmentIndex + 1}
                </span>
                <ChevronDown className="h-4 w-4" />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-2 border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-2">产品</th>
                        <th className="text-right p-2">需求</th>
                        <th className="text-right p-2">库存</th>
                        <th className="text-right p-2">建议采购</th>
                        <th className="text-right p-2">分配</th>
                        <th className="text-right p-2">单价</th>
                        <th className="p-2">币种</th>
                        <th className="text-right p-2">汇率</th>
                        <th className="text-right p-2">小计</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignment.line_items.map((item) => (
                        <tr key={item.line_item_id} className="border-t">
                          <td className="p-2">
                            <div className="font-medium">{item.product_code}</div>
                            <div className="text-xs text-muted-foreground">{item.product_name}</div>
                          </td>
                          <td className="text-right p-2">{item.original_quantity || 0}</td>
                          <td className="text-right p-2">
                            <span className={cn(
                              "font-medium",
                              item.inventory?.available_quantity >= (item.original_quantity || 0)
                                ? "text-success"
                                : item.inventory?.available_quantity > 0
                                  ? "text-amber-600"
                                  : "text-destructive"
                            )}>
                              {item.inventory?.available_quantity ?? 0}
                            </span>
                          </td>
                          <td className="text-right p-2">
                            <span className={cn(
                              (item.so_quantity || 0) > 0 ? "font-bold text-primary" : "text-muted-foreground"
                            )}>
                              {item.so_quantity || 0}
                            </span>
                          </td>
                          <td className="text-right p-2">
                            <div>
                              <Input
                                type="number"
                                value={item.allocated_quantity}
                                onChange={(e) =>
                                  updateLineItem(
                                    assignment.id,
                                    item.line_item_id,
                                    'allocated_quantity',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                min={0}
                                step={0.01}
                                className={cn(
                                  "w-20 text-right",
                                  item.allocated_quantity > (item.original_quantity || 0) && "bg-amber-50"
                                )}
                              />
                              {item.allocated_quantity > (item.original_quantity || 0) && (
                                <div className="text-xs text-amber-600 mt-1">
                                  超出 {(item.allocated_quantity - (item.original_quantity || 0)).toFixed(2)} 件将入库
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="text-right p-2">
                            <Input
                              type="number"
                              value={item.unit_price}
                              onChange={(e) =>
                                updateLineItem(
                                  assignment.id,
                                  item.line_item_id,
                                  'unit_price',
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              min={0}
                              step={0.01}
                              className="w-20 text-right"
                            />
                          </td>
                          <td className="p-2">
                            <Select
                              value={item.currency}
                              onValueChange={(value) =>
                                updateLineItem(
                                  assignment.id,
                                  item.line_item_id,
                                  'currency',
                                  value
                                )
                              }
                            >
                              <SelectTrigger className="w-20">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="CNY">CNY</SelectItem>
                                <SelectItem value="USD">USD</SelectItem>
                                <SelectItem value="EUR">EUR</SelectItem>
                                <SelectItem value="JPY">JPY</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="text-right p-2">
                            <Input
                              type="number"
                              value={item.fx_rate}
                              onChange={(e) =>
                                updateLineItem(
                                  assignment.id,
                                  item.line_item_id,
                                  'fx_rate',
                                  parseFloat(e.target.value) || 1.0
                                )
                              }
                              min={0.01}
                              step={0.01}
                              className="w-16 text-right"
                            />
                          </td>
                          <td className="text-right p-2 font-medium">
                            ¥{(item.allocated_quantity * item.unit_price * item.fx_rate).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>
    );
  };

  const renderPreviewStep = () => (
    <div className="py-4 space-y-4">
      <h3 className="text-lg font-semibold">转换预览</h3>

      {previewData && (
        <>
          <Card>
            <CardContent className="p-4">
              <h4 className="font-medium mb-3">转换摘要</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="text-muted-foreground">
                  销售订单: {previewData.sales_order_id}
                </div>
                <div className="text-muted-foreground">
                  将创建采购订单: {previewData.total_pos} 个
                </div>
                <div className="text-muted-foreground">
                  总金额: ¥{previewData.total_amount.toFixed(2)}
                </div>
                <div className="text-muted-foreground">
                  分拆策略: {splittingStrategy === 'manual' ? '手动分拆' : '自动分拆'}
                </div>
              </div>
            </CardContent>
          </Card>

          {previewData.po_summaries.map((poSummary, index) => (
            <Card key={index}>
              <CardContent className="p-4">
                <h4 className="font-medium mb-2">
                  采购订单 #{poSummary.po_index} - {poSummary.supplier_name}
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="text-muted-foreground">
                    产品行项目: {poSummary.line_item_count} 个
                  </div>
                  <div className="text-muted-foreground">
                    订单金额: ¥{poSummary.total_amount.toFixed(2)}
                  </div>
                  <div className="col-span-2 text-muted-foreground">
                    币种分布: {Object.entries(poSummary.currency_breakdown)
                      .map(([currency, amount]) => `${currency} ${amount.toFixed(2)}`)
                      .join(', ')}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </div>
  );

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Split className="h-5 w-5" />
            销售单转采购单 - 分拆工具
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            销售订单: {salesOrderData?.sales_order_id} | 客户: {salesOrderData?.client_name}
          </p>
        </DialogHeader>

        <Separator />

        <div className="py-4">
          {loading && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {validationErrors.length > 0 && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>
                <div className="font-medium mb-1">请修正以下问题:</div>
                <ul className="list-disc list-inside">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Stepper */}
          <div className="flex items-center justify-between mb-6">
            {steps.map((label, index) => (
              <div key={label} className="flex items-center">
                <div className={cn(
                  "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium",
                  index <= activeStep
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                )}>
                  {index + 1}
                </div>
                <span className={cn(
                  "ml-2 text-sm hidden sm:inline",
                  index <= activeStep ? "text-foreground" : "text-muted-foreground"
                )}>
                  {label}
                </span>
                {index < steps.length - 1 && (
                  <div className={cn(
                    "w-8 sm:w-16 h-0.5 mx-2",
                    index < activeStep ? "bg-primary" : "bg-muted"
                  )} />
                )}
              </div>
            ))}
          </div>

          {!loading && renderStepContent(activeStep)}
        </div>

        <Separator />

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            取消
          </Button>

          <div className="flex-1" />

          {activeStep > 0 && (
            <Button variant="outline" onClick={handleBack} disabled={loading}>
              上一步
            </Button>
          )}

          {activeStep < steps.length - 1 ? (
            <Button onClick={handleNext} disabled={loading}>
              下一步
            </Button>
          ) : (
            <Button
              onClick={handleConvert}
              disabled={loading || validationErrors.length > 0}
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <ShoppingCart className="h-4 w-4 mr-2" />
              确认转换
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SoPoSplittingDialog;
